// FO-319 (batch-35) → FO-366 (batch-41) — NMBRS-periode-import-export: het
// kolomcontract, de FO-318-categorieën, de looncode-mapping-DTO's + de
// overzicht- en per-BV-preview-DTO's.
//
// Het aangeleverde werkboek "payroll-export-20-04-2026 Numbers export.xls"
// (Appreo-sjabloon "Nmbrs Import") is sinds FO-366 het CONTRACT: tien
// tabbladen, waarvan STREEP `LooncomponentenVar` vult - twee koprijen (rij 1
// labels, rij 2 codes), kolomvolgorde + codes + labels EXACT zoals hieronder
// vastgelegd (bron: FO-366 §2-tabel, leidend). Het werkboek-bestand zit niet in
// de repo; de tabbladen-/kolomdefinitie hieronder IS de sjabloonstructuur
// (code-definitie, FO-366 §11.2). Wijzigt Nmbrs iets, dan is dit de enige plek.
// De oude FO-319-codes die niet in het werkboek voorkomen (U2190, U6100) zijn uit
// de kolomset verwijderd; een DB-mapping die er nog naar wijst telt als NIET-GEMAPT
// (zichtbare blokkade, nooit een stille som). U5565, U5566, U5569 en U3070 zijn op
// aanwijzing van de PO (30-10-2026) weer kolommen: achteraan, zodat de posities van
// de bestaande werkboek-kolommen gelijk blijven.

import { z } from 'zod'

// ─── De FO-318-engine-categorieën (spiegelt de Prisma-enum NmbrsLooncodeCategorie) ─

export const NMBRS_LOONCODE_CATEGORIE = [
  'EXTRA_UREN',
  'OPROEP_UREN',
  'REISUREN',
  'PLOEG_2',
  'PLOEG_3',
  'PLOEG_5',
  'ZATERDAG',
  'ZONDAG',
  'OVERUREN_25',
  'OVERUREN_50',
  'OVERUREN_100',
  'ZIEKTE_70',
  'ZIEKTE_90',
  'ZIEKTE_100',
] as const
export type NmbrsLooncodeCategorie = (typeof NMBRS_LOONCODE_CATEGORIE)[number]

export const NMBRS_LOONCODE_CATEGORIE_LABELS: Record<NmbrsLooncodeCategorie, string> = {
  EXTRA_UREN: 'Extra uren (overschot)',
  OPROEP_UREN: 'Uren oproepkrachten',
  REISUREN: 'Reisuren',
  PLOEG_2: 'Ploegentoeslag 2-ploegen',
  PLOEG_3: 'Ploegentoeslag 3-ploegen',
  PLOEG_5: 'Ploegentoeslag 5-ploegen',
  ZATERDAG: 'Zaterdagtoeslag',
  ZONDAG: 'Zondagtoeslag',
  OVERUREN_25: 'Overuren-staffel 25%',
  OVERUREN_50: 'Overuren-staffel 50%',
  OVERUREN_100: 'Overuren-staffel 100%',
  ZIEKTE_70: 'Ziekte-uren 70%',
  ZIEKTE_90: 'Ziekte-uren 90%',
  ZIEKTE_100: 'Ziekte-uren 100%',
}

// ─── Het kolomcontract van sheet `LooncomponentenVar` (rij 1 labels, rij 2 codes) ─
//
// Identificatie-kolommen dragen GEEN U-code (rij 2 is voor die kolommen de
// veldnaam, gelijk aan rij 1 in het model). De looncode-kolommen dragen de U-code
// in rij 2. Beide rijen zijn CONSTANT (bron: FO-319) — geen runtime-generatie.

/** Eén kolom in de NMBRS-sheet: veld-id + koprij-1-label + koprij-2-code. */
export interface NmbrsKolomDef {
  /** Interne id (voor de builder). */
  id: string
  /** Rij 1 (labels). */
  label: string
  /** Rij 2 (codes) — U-code voor looncode-kolommen; veldnaam voor identificatie. */
  code: string
  /** Identificatie-kolom (Debiteurnr..Dagen) vs waarde-kolom (U-/L-codes). */
  soort: 'IDENTIFICATIE' | 'LOONCODE'
}

/** De vaste identificatie-kolommen (in volgorde) — FO-366 §2: kolom 1-6. */
export const NMBRS_IDENTIFICATIE_KOLOMMEN: readonly NmbrsKolomDef[] = [
  { id: 'debiteurnr', label: 'Debiteurnr', code: 'Debiteurnr', soort: 'IDENTIFICATIE' },
  { id: 'bedrijfnr', label: 'Bedrijfnr', code: 'Bedrijfnr', soort: 'IDENTIFICATIE' },
  { id: 'persnr', label: 'Persnr', code: 'Persnr', soort: 'IDENTIFICATIE' },
  { id: 'naam', label: 'Naam', code: 'Naam', soort: 'IDENTIFICATIE' },
  { id: 'periode', label: 'Periode', code: 'Periode', soort: 'IDENTIFICATIE' },
  { id: 'dagen', label: 'Dagen', code: 'Dagen', soort: 'IDENTIFICATIE' },
] as const

/**
 * De looncode-kolommen (in volgorde), codes + labels EXACT het werkboek
 * (FO-366 §2-tabel, leidend). U-codes zijn uren-kolommen; L-codes (bonus) zijn
 * bedragen-kolommen — het werkboek ondersteunt beide, de cel-typering is getal.
 */
export const NMBRS_LOONCODE_KOLOMMEN: readonly NmbrsKolomDef[] = [
  { id: 'U2100', label: 'Gewerkte uren', code: 'U2100', soort: 'LOONCODE' },
  { id: 'U3090', label: 'Ziekte uren 90%', code: 'U3090', soort: 'LOONCODE' },
  { id: 'U3100', label: 'Ziekte uren 100%', code: 'U3100', soort: 'LOONCODE' },
  { id: 'U5562', label: 'Toeslaguren 30%', code: 'U5562', soort: 'LOONCODE' },
  { id: 'U5564', label: 'Toeslaguren 50%', code: 'U5564', soort: 'LOONCODE' },
  { id: 'U5560', label: 'Reisuren 100%', code: 'U5560', soort: 'LOONCODE' },
  { id: 'U6125', label: 'Overuren 125%', code: 'U6125', soort: 'LOONCODE' },
  { id: 'U6150', label: 'Overuren 150%', code: 'U6150', soort: 'LOONCODE' },
  { id: 'U6200', label: 'Overuren 200%', code: 'U6200', soort: 'LOONCODE' },
  { id: 'U6128', label: 'Ploegendienst 130%', code: 'U6128', soort: 'LOONCODE' },
  { id: 'L3045', label: 'Bonus bruto', code: 'L3045', soort: 'LOONCODE' },
  { id: 'L5001', label: 'Bonus WKR', code: 'L5001', soort: 'LOONCODE' },
  { id: 'U5565', label: 'Toesl.uren 15%', code: 'U5565', soort: 'LOONCODE' },
  { id: 'U5566', label: 'Toesl.uren 22%', code: 'U5566', soort: 'LOONCODE' },
  { id: 'U5569', label: 'Reisuren', code: 'U5569', soort: 'LOONCODE' },
  { id: 'U3070', label: 'Ziekte-uren 70%', code: 'U3070', soort: 'LOONCODE' },
] as const

/**
 * FO-366 §11.2 — de tien tabbladen van het Nmbrs-importwerkboek, in volgorde.
 * STREEP vult alleen `LooncomponentenVar`; de overige tabbladen gaan als leeg
 * sjabloon mee zodat Nmbrs het werkboek herkent. Code-definitie (het binaire
 * sjabloon is niet meegeleverd) — wijzigt het sjabloon, dan is dit de plek.
 */
export const NMBRS_WERKBOEK_TABBLADEN = [
  'Start',
  'Werknemers',
  'WerknemerHR',
  'Kostenplaatsen',
  'LooncomponentenVast',
  'LooncomponentenVar',
  'Verlof',
  'Branches',
  'KostenverdelingVast',
  'KostenverdelingLooncomponenten',
] as const

/** Alle kolommen in volgorde (identificatie + looncodes). */
export const NMBRS_KOLOMMEN: readonly NmbrsKolomDef[] = [
  ...NMBRS_IDENTIFICATIE_KOLOMMEN,
  ...NMBRS_LOONCODE_KOLOMMEN,
]

/** De geldige NMBRS-kolomcodes (U-codes) waarnaar een categorie mag mappen. */
export const NMBRS_KOLOM_CODES = NMBRS_LOONCODE_KOLOMMEN.map((k) => k.code)
export type NmbrsKolomCode = (typeof NMBRS_LOONCODE_KOLOMMEN)[number]['code']

/** Koprij 1 (labels) als vaste array — voor de byte-vergelijk-test. */
export const NMBRS_KOPRIJ_LABELS: readonly string[] = NMBRS_KOLOMMEN.map((k) => k.label)
/** Koprij 2 (codes) als vaste array — voor de byte-vergelijk-test. */
export const NMBRS_KOPRIJ_CODES: readonly string[] = NMBRS_KOLOMMEN.map((k) => k.code)

export const NMBRS_SHEET_NAAM = 'LooncomponentenVar'

// ─── Seed-mapping (VOORSTEL — salaris bevestigt via de instellingen) ──────────
//
// FO-366 §3.1.2: de mapping volgt het werkboek. Werkuren (overschot + oproep)
// sommeren op U2100 "Gewerkte uren"; reisuren → U5560; de DAG-staffel →
// U6125/U6150/U6200; ziekte 90/100 → U3090/U3100. Voor de toeslagcategorieën
// geeft het werkboek drie kolommen (U5562 30% / U5564 50% / U6128 ploegendienst
// 130%) maar NIET de interne toewijzing — die bevestigt SALARIS in de
// mapping-UI (§3.1.2/A4). Alleen de exacte matches zijn als voorstel geseed:
// ZATERDAG → U5564 (50%) en PLOEG_5 → U6128 (ploegendienst, 130% = +30%).
// PLOEG_2 (15%) → U5565, PLOEG_3 (22%) → U5566 en ZIEKTE_70 → U3070 zijn door de
// PO aangewezen (30-10-2026). `null` = bewust GEEN voorstel: ZONDAG (100%) heeft
// geen passende werkboek-kolom — mét uren blokkeert de export zichtbaar tot SALARIS
// kiest. Een stil verkeerd toeslagpercentage is erger dan een geblokkeerde export.

export const NMBRS_LOONCODE_SEED: Record<NmbrsLooncodeCategorie, NmbrsKolomCode | null> = {
  EXTRA_UREN: 'U2100', // sommeert met OPROEP_UREN op "Gewerkte uren"
  OPROEP_UREN: 'U2100',
  REISUREN: 'U5560',
  PLOEG_2: 'U5565', // 15%
  PLOEG_3: 'U5566', // 22%
  PLOEG_5: 'U6128', // ploegendienst 130% (= +30%, exact het 5-ploegenpercentage)
  ZATERDAG: 'U5564', // 50% (exact)
  ZONDAG: null, // 100% — geen werkboek-kolom; SALARIS beslist (of Nmbrs-kolom erbij)
  OVERUREN_25: 'U6125',
  OVERUREN_50: 'U6150',
  OVERUREN_100: 'U6200',
  ZIEKTE_70: 'U3070',
  ZIEKTE_90: 'U3090',
  ZIEKTE_100: 'U3100',
}

// ─── Mapping-DTO's + schema's ─────────────────────────────────────────────────

export interface NmbrsLooncodeMappingDTO {
  id: string
  categorie: NmbrsLooncodeCategorie
  categorieLabel: string
  /**
   * FO-366 — null = geen kolom gekozen (geen werkboek-kolom voor deze categorie
   * of een vervallen legacy-code): de export blokkeert zodra er uren op staan.
   */
  kolomCode: string | null
  /** Label van de kolom uit het werkboek ("Gewerkte uren"); null = geen kolom. */
  kolomLabel: string | null
  actief: boolean
  updatedAt: string | null
}

/** Overzicht van alle mappings + de gedetecteerde sommaties (kolom ← meerdere cat). */
export interface NmbrsLooncodeMappingOverzichtDTO {
  mappings: NmbrsLooncodeMappingDTO[]
  /** Per kolomcode de categorieën die erop sommeren (>1 = sommatie, zichtbaar). */
  sommaties: Array<{ kolomCode: string; kolomLabel: string; categorieen: string[] }>
}

export const nmbrsLooncodeMappingUpdateSchema = z.object({
  categorie: z.enum(NMBRS_LOONCODE_CATEGORIE),
  kolomCode: z.enum(NMBRS_KOLOM_CODES as unknown as [string, ...string[]]),
  actief: z.boolean().optional(),
})
export type NmbrsLooncodeMappingUpdate = z.infer<typeof nmbrsLooncodeMappingUpdateSchema>

// ─── Overzicht-scherm (FO-319 §2.1/§2.2) ──────────────────────────────────────

export const PAYROLL_OVERZICHT_PERIODE_TYPE = ['DAG', 'WEEK', 'MAAND', 'VRIJ'] as const
export type PayrollOverzichtPeriodeType = (typeof PAYROLL_OVERZICHT_PERIODE_TYPE)[number]

/** De uurtype-assen: alle FO-318-categorieën of het totaaloverzicht. */
export const PAYROLL_OVERZICHT_WEERGAVE = ['UREN', 'BEDRAGEN'] as const
export type PayrollOverzichtWeergave = (typeof PAYROLL_OVERZICHT_WEERGAVE)[number]

// payroll-werkruimte 18→8 — server-side sortering: het overzicht is
// server-gepagineerd, dus sorteren moet vóór de page-slice gebeuren.
export const PAYROLL_OVERZICHT_SORT = ['personeelsnummer', 'naam', 'totaal'] as const
export type PayrollOverzichtSort = (typeof PAYROLL_OVERZICHT_SORT)[number]

export const payrollOverzichtQuerySchema = z
  .object({
    periodeType: z.enum(PAYROLL_OVERZICHT_PERIODE_TYPE).default('MAAND'),
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12).optional(),
    week: z.coerce.number().int().min(1).max(53).optional(),
    day: z.string().optional(), // yyyy-mm-dd voor DAG
    vanaf: z.string().optional(), // yyyy-mm-dd voor VRIJ
    tot: z.string().optional(), // yyyy-mm-dd voor VRIJ
    employeeId: z.string().optional(),
    employeeIds: z.string().optional(), // csv
    entityId: z.string().optional(),
    afdeling: z.string().optional(),
    weergave: z.enum(PAYROLL_OVERZICHT_WEERGAVE).default('UREN'),
    // Defaults = het oude gedrag (personeelsnummer oplopend).
    sort: z.enum(PAYROLL_OVERZICHT_SORT).default('personeelsnummer'),
    order: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((v) => v.periodeType !== 'MAAND' || v.month != null, {
    message: 'month verplicht bij periodeType MAAND',
    path: ['month'],
  })
export type PayrollOverzichtQuery = z.infer<typeof payrollOverzichtQuerySchema>

/** Eén rij (medewerker) × categorie-kolommen. Uren OF bedragen (weergave). */
export interface PayrollOverzichtRowDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  /** Waarde per categorie (uren of, achter de kosten-gate, bedragen). */
  waarden: Record<NmbrsLooncodeCategorie, number>
  /** Rij-totaal (som over de categorieën). */
  totaal: number
}

// ─── NMBRS-export preview (FO-303-P3) ─────────────────────────────────────────

/** Eén rij zoals hij in het bestand komt (preview = bestand). */
export interface NmbrsExportPreviewRowDTO {
  debiteurnr: string
  bedrijfnr: string
  persnr: string
  naam: string
  periode: string
  /** Per U-code de gesommeerde uren (alleen > 0). */
  looncodeUren: Record<string, number>
  warnings: string[]
}

export interface NmbrsExportOngemapteCategorieDTO {
  categorie: NmbrsLooncodeCategorie
  label: string
  totaalUren: number
}

/**
 * FO-366 §3.2 — één BV-sectie van de per-BV-preview: de kop (naam + Nmbrs-
 * nummers), de rijen exact in werkboekvorm (in-dienst-medewerkers krijgen ook
 * zonder uren een rij, A3), de totalen per kolom en de blokkades die de
 * download voor déze BV zouden weigeren.
 */
export interface NmbrsExportBvSectieDTO {
  /** null = medewerkers zonder gekoppelde BV (sectie toont blokkade). */
  entityId: string | null
  bvNaam: string
  debiteurnr: string
  bedrijfnr: string
  /** BV-niveau blokkades (bv. "vul de Nmbrs-nummers in op de entiteit"). */
  blokkades: string[]
  rows: NmbrsExportPreviewRowDTO[]
  /** Totaal per kolomcode over de sectie (alleen codes met een som > 0). */
  totalen: Record<string, number>
  /** Rijen met een rij-blokkade (Persnr/loon ontbreekt) binnen deze sectie. */
  waarschuwingsRijen: number
  /** FO-351-naam van de download voor deze BV. */
  bestandsnaam: string
  /** true = de download voor deze BV is (zonder expliciete keuze) geweigerd. */
  downloadGeblokkeerd: boolean
}

export interface NmbrsExportPreviewDTO {
  year: number
  month: number
  periode: string
  isVastgelegd: boolean
  rows: NmbrsExportPreviewRowDTO[]
  /** FO-366 — per BV een sectie in werkboekvorm (incl. lege in-dienst-rijen). */
  bvSecties: NmbrsExportBvSectieDTO[]
  /** Niet-gemapte categorieën met waarde > 0 → export MOET blokkeren. */
  ongemapt: NmbrsExportOngemapteCategorieDTO[]
  /** Aantal rijen met een ontbrekend Persnr/Debiteur-/Bedrijfnr (vereisen keuze). */
  waarschuwingsRijen: number
  /** Deterministisch versietoken (drift-detectie bij download). */
  token: string
  /** Voorgestelde bestandsnaam (FO-152). */
  bestandsnaam: string
}

export const nmbrsExportQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
  /**
   * true = laat rijen met ontbrekend Persnr/Debiteur-/Bedrijfnr weg (expliciete
   * keuze). Query-param-veilig: alleen `'true'`/`'1'`/`true` telt als true
   * (z.coerce.boolean maakt van de STRING `'false'` ook `true` — vermeden).
   */
  laatOnvolledigeRijenWeg: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === 'true' || v === '1'),
  /** Versietoken uit de preview — download 409't bij drift (tussentijdse wijziging). */
  token: z.string().optional(),
  /** FO-366 — download voor één BV (werkboek per debiteur/bedrijf). */
  entityId: z.string().optional(),
})
export type NmbrsExportQuery = z.infer<typeof nmbrsExportQuerySchema>

export interface PayrollOverzichtDTO {
  periodeType: PayrollOverzichtPeriodeType
  periodeLabel: string
  weergave: PayrollOverzichtWeergave
  /** Of de kosten-/salaris-gate bedragen toestaat (anders alleen uren). */
  bedragenToegestaan: boolean
  /**
   * true wanneer de getoonde bedragen INDICATIEF zijn (uren x globaal uurtarief x
   * CAO-categoriepercentage) i.p.v. de exacte, persoonlijk-uurloon-gedreven
   * reservering-bedragen. De pariteit overzicht = reservering = export geldt voor
   * de UREN; de exacte euro's staan in de reservering. Alleen relevant bij
   * weergave = BEDRAGEN.
   */
  bedragenIndicatief: boolean
  /** Of deze periode uit een vastgelegd snapshot komt (pariteit) of live is. */
  isVastgelegd: boolean
  rows: PayrollOverzichtRowDTO[]
  /** Totalenrij (som per categorie over alle rijen). */
  totalen: Record<NmbrsLooncodeCategorie, number>
  page: number
  pageSize: number
  totalRows: number
}
